import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import { AppModule } from '@/store/modules/app';
import { Languages } from '@/types/languagesType';
import DefaultTemplate from '@/layouts/DefaultTemplate.vue';
import CabinetLayout from '@/layouts/CabinetLayout.vue';
import Welcome from '@/views/WelcomeView.vue';
import { UserModule } from '@/store/modules/user';
import { ThemesModule } from '@/store/modules/themes';
import { getByDomain } from '@/config/sites';
import { UserType } from '@/types/ServerToken';

Vue.use(VueRouter);

// maps the `cabinet` query param to the internal user type
const CABINET_TYPE_MAP: Record<string, number> = {
  applicant: UserType.applicant,
  consultant: UserType.consultant,
  company: UserType.company,
};

const routes: Array<RouteConfig> = [
  {
    path: '',
    component: DefaultTemplate,
    children: [
      {
        path: '/',
        name: 'Welcome',
        component: Welcome,
      },
      {
        path: '/jobs',
        name: 'Jobs',
        component: () => import('../views/HomeView.vue'),
      },
      {
        path: '/workers',
        name: 'Workers',
        component: () => import('../views/SkilledView.vue'),
      },
      {
        path: '/company',
        redirect: { path: '/jobs', query: { tab: 'company' } },
      },
      {
        path: '/map',
        redirect: { path: '/jobs', query: { tab: 'map' } },
      },
      {
        path: '/impressum',
        name: 'Impressum',
        component: () => import('../views/ImpressumView.vue'),
      },
      {
        path: '/iframe-workers',
        name: 'WorkersIframe',
        component: () => import('../views/Iframe/WorkersIframeView.vue'),
      },
      {
        path: '/courses',
        name: 'Courses',
        component: () => import('../views/CoursesView.vue'),
      },
      {
        path: '/ausbildung',
        redirect: { path: '/jobs', query: { tab: 'ausbildung' } },
      },
      {
        path: '/orte',
        redirect: { path: '/jobs', query: { tab: 'orte' } },
      },
      {
        path: '/confirm_email',
        name: 'confirmEmail',
        component: () => import('../views/ConfirmEmail.vue'),
      },
      {
        path: '/login',
        name: 'LoginLegacy',
        component: Welcome,
      },
    ],
  },
  {
    // isolated standalone page — no app header/footer/sidebars
    path: '/stat',
    name: 'Stat',
    component: () => import('../views/StatView.vue'),
  },
  {
    path: '',
    component: CabinetLayout,
    children: [
      {
        path: '/cabinet',
        name: 'Cabinet',
        component: () => import('../views/Profile/CabinetEntryView.vue'),
      },
    ],
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach(async(to, from, next) => {
  const legacyAuthHash = to.hash.replace('#', '');
  if (
    to.path === '/login'
    && (legacyAuthHash === 'a-login' || legacyAuthHash === 'f-login')
    && !to.query.auth
  ) {
    next({
      path: '/',
      query: {
        ...to.query,
        auth: legacyAuthHash,
      },
      replace: true,
    });
    return;
  }

  const ThemeDetection = getByDomain();

  if (ThemeDetection) {
    AppModule.ActionSid(ThemeDetection.sid);

    // add theme
    ThemesModule.SetTheme(ThemeDetection);
  }

  // language query redirect
  if (to.query.lang && Object.values(Languages).includes(to.query.lang as Languages)) {
    AppModule.ActionLanguage(String(to.query.lang) as Languages);
  } else {
    // if (ThemeDetection && ThemeDetection.language) {
    //   AppModule.ActionLanguage(ThemeDetection.language);
    // }
    // @ts-ignore
    next({ ...to, query: { ...to.query, lang: AppModule.language }, replace: true });
    return;
  }

  // auto-login via deep link: /?access_token={token}&cabinet=applicant|consultant|company
  if (to.path === '/' && to.query.access_token && to.query.cabinet) {
    const cabinet = String(to.query.cabinet);
    const type = CABINET_TYPE_MAP[cabinet];
    if (type !== undefined) {
      try {
        await UserModule.LoginBySingleUseToken({
          single_use_token: String(to.query.access_token),
          sid: AppModule.sid,
          type,
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('single_use_token login error:', e);
      }
      // strip the token from the URL so it is not exposed in the address bar / history
      const {
        access_token, cabinet: _cabinet, refresh_token, expires, ...restQuery
      } = to.query;
      next({ path: '/', query: { ...restQuery, lang: AppModule.language }, replace: true });
      return;
    }
  }

  if (!UserModule.accessTokenApplicant && !UserModule.accessTokenConsultant && !UserModule.accessTokenCompany) {
    if (to.path === '/cabinet') {
      next({ path: '/' });
    }
  }

  if (to.query.iframe) {
    AppModule.SetIsIframe(true);
  }

  if (to.query.sid) {
    AppModule.ActionLanguageSid(String(to.query.sid));
  }
  next();
});

export default router;
